import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Building2, Bookmark as BookmarkIcon } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Bookmarks = () => {
  const { token } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookmarks();
  }, [token]);

  const fetchBookmarks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bookmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBookmark = async (listingId) => {
    try {
      await axios.delete(`${API_URL}/api/bookmarks/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(listings.filter(listing => listing.id !== listingId));
      toast.success('Bookmark removed');
    } catch (error) {
      toast.error('Failed to remove bookmark');
    }
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)}Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)}L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="bookmarks-title">
            Bookmarked Listings
          </h1>
          <p className="text-lg text-slate-600">
            Your saved properties for quick access
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            <div className="text-slate-600">Loading bookmarks...</div>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200" data-testid="empty-state">
            <BookmarkIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <div className="text-xl font-semibold text-slate-900 mb-2">No bookmarks yet</div>
            <div className="text-slate-600">Start bookmarking listings to save them here</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookmarks-list">
            {listings.map((listing, index) => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 smooth-transition group"
                data-testid={`bookmark-${index}`}
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-64 h-48 md:h-auto bg-slate-100 flex-shrink-0">
                    {listing.image_url ? (
                      <img
                        src={listing.image_url}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building2 className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
                          {listing.title}
                        </h3>
                        <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{listing.location}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="font-medium">PIN:</span>
                            <span>{listing.pincode}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(listing.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        <div className="text-2xl font-bold text-slate-900">
                          {formatPrice(listing.price)}
                        </div>
                        <button
                          onClick={() => handleRemoveBookmark(listing.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 smooth-transition"
                          data-testid={`remove-bookmark-${index}`}
                        >
                          <BookmarkIcon className="w-4 h-4 fill-current" />
                          <span className="text-sm font-medium">Remove</span>
                        </button>
                      </div>
                    </div>

                    <p className="text-slate-600 line-clamp-2">
                      {listing.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};